import { useTranslation } from 'react-i18next';

import useAiChatToolLabel from '../../hooks/useAiChatToolLabel';
import useAiChatToolStatus from '../../hooks/useAiChatToolStatus';
import useOpenAgentSession from '../../hooks/useOpenAgentSession';
import readAgentSubTypeKey from '../../utils/readAgentSubTypeKey';
import AiChatToolCard from '../AiChatToolCard';

import type { ReactElement } from 'react';
import type IAgentSessionToolCall from '../../interfaces/IAgentSessionToolCall';

import styles from '../../sass/AiChat.module.scss';

/**
 * The `createAgentSession` and `updateAgentSessionUnits` tool calls: an N-tier discovery session
 * of a map made, or its tabs changed. One component: the two answer with the same shape.
 *
 * A step row: the header names the agent and how many tabs the session has, and the open button
 * sits under it. Unit ids are tool-side ids and are never shown. A refusal is an answer for the
 * model, not a failure: the row shows the reason on one line.
 *
 * The session is a normal one of the agent panel — the chat does not own it. On a live part
 * `useAiChatToolActions` refreshes its cache and opens nothing; only the button opens its modal.
 */
function AgentSessionToolCall({ part }: IAgentSessionToolCall): ReactElement {
  const { t } = useTranslation();
  const isCreate = part.type === 'tool-createAgentSession';
  const label = useAiChatToolLabel(isCreate ? 'createAgentSession' : 'updateAgentSessionUnits');
  const status = useAiChatToolStatus(part.state);
  const openAgentSession = useOpenAgentSession();

  const output = part.state === 'output-available' ? part.output : undefined;

  // Under the header: which agent or session while the call runs, or the reason of a refusal.
  const readNote = (): string | undefined => {
    if (output) {
      return output.ok ? undefined : output.reason;
    }

    if (part.type === 'tool-createAgentSession') {
      return part.input?.subType ? t(readAgentSubTypeKey(part.input.subType)) : undefined;
    }

    return part.input?.sessionId
      ? t('aiChat.tools.agentSessionFallbackName', { id: part.input.sessionId })
      : undefined;
  };

  const readSummary = (): string | undefined => {
    if (!output) {
      return status;
    }

    if (!output.ok) {
      return t('aiChat.tools.agentSessionRefused');
    }

    return t(isCreate ? 'aiChat.tools.agentSessionCreated' : 'aiChat.tools.agentSessionUpdated', {
      agent: t(readAgentSubTypeKey(output.subType)),
      tabs: t('aiChat.tools.agentTabCount', { count: output.tabs.length }),
    });
  };

  const handleOpen = () => {
    if (output?.ok) {
      openAgentSession({
        valueChainMapId: output.valueChainMapId, sessionId: output.sessionId, subType: output.subType,
      });
    }
  };

  // After a create the model points the user here.
  const openButton = output?.ok ? (
    <button type="button" className={styles.toolToggle} onClick={handleOpen}>
      {t('aiChat.tools.agentOpenSession')}
    </button>
  ) : undefined;

  return (
    <AiChatToolCard
      name={label}
      status={readSummary()}
      content={openButton}
      errorText={part.errorText}
    >
      {readNote()}
    </AiChatToolCard>
  );
}

export default AgentSessionToolCall;
